import axios, { Method } from 'axios'
import Vault from './Vault'
import { API, CLIENT_TYPE } from 'config/constants/env'
import { get } from 'lodash'
import AuthenticationControllerService from 'smart-components/authentication-controller/Service'

export interface IRequestOptions {
  unauthorizedErrorExpected: boolean
}

async function executeAxiosRequest<T>(
  url: string,
  method: Method,
  data: any = null,
  options?: IRequestOptions,
  customHeaders?: any
): Promise<[T | null, number, string | null]> {
  const headers: { [key: string]: string } = {
    'X-Client-Type': CLIENT_TYPE,
    ...customHeaders
  }

  const token = Vault.getAccessToken()

  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  try {
    const response = await axios({
      baseURL: API.BASE_URL,
      url,
      method,
      headers,
      data
    })

    return [response.data as T, response.status, null]
  } catch (error) {
    if (error.response) {
      if (error.response.status === 401 && (!options || !options.unauthorizedErrorExpected)) {
        AuthenticationControllerService.signOut()
      }

      const errorMessage = [404, 500].includes(error.response.status)
        ? error.message
        : get(error, 'response.data') || error.message
      return [null, error.response.status, errorMessage]
    }

    return [null, 0, error.message]
  }
}

export default class Http {
  static async get<T>(url: string, options?: IRequestOptions): Promise<[T | null, number, string | null]> {
    return executeAxiosRequest<T>(url, 'GET', null, options)
  }

  static async post<TData, TResult>(
    url: string,
    data: TData | FormData,
    options?: IRequestOptions,
    headers?: any
  ): Promise<[TResult | null, number, string | null]> {
    return executeAxiosRequest<TResult>(url, 'POST', data, options, headers)
  }

  static async put<TData, TResult>(
    url: string,
    data: TData,
    options?: IRequestOptions,
    headers?: any
  ): Promise<[TResult | null, number, string | null]> {
    return executeAxiosRequest<TResult>(url, 'PUT', data, options, headers)
  }

  static async $delete<T>(url: string, options?: IRequestOptions): Promise<[T | null, number, string | null]> {
    return executeAxiosRequest<T>(url, 'DELETE', null, options)
  }
}
